import { useId } from 'react'
import { categoryGradient } from '@core/utils/categoryVisual'

export interface ProductTileImageProps {
  /** Имя товара: используется для градиента, если категория не передана. */
  name: string
  /** Категория: из неё берётся градиент, если отдела нет. */
  categoryName?: string
  /** Верхняя категория (отдел): приоритетный источник цвета. */
  categoryParent?: string | null
  /** Порядковый номер в категории. */
  ordinal?: number
  /** Загруженная картинка товара (опционально). */
  imageUrl?: string
  /** Размер квадрата в px. */
  size?: number
  /** Растянуть на всю ширину плитки (квадрат по aspect-ratio). */
  fullWidth?: boolean
  className?: string
}

/**
 * Плитка товара: градиент категории и крупный порядковый номер.
 * Если есть загруженная картинка — она, а номер рисуется бейджем.
 */
export function ProductTileImage({
  name,
  categoryName,
  categoryParent,
  ordinal,
  imageUrl,
  size = 64,
  fullWidth = false,
  className
}: ProductTileImageProps) {
  const rawId = useId()
  const gradientId = `pt-${rawId.replace(/[^a-zA-Z0-9_-]/g, '')}`
  const gradient = categoryGradient(categoryName || name, null, categoryParent)
  // Номер — всегда число: буквенный fallback («М» вместо номера) сбивал
  // кассира, если у товара ещё не назначен categoryOrdinal.
  const label = ordinal != null ? String(ordinal) : '?'

  if (imageUrl) {
    return (
      <div
        className={`relative overflow-hidden rounded-md bg-soft ${
          fullWidth ? 'aspect-square w-full' : ''
        } ${className ?? ''}`}
        style={fullWidth ? undefined : { width: size, height: size }}
      >
        <img src={imageUrl} alt="" className="h-full w-full object-cover" />
        {ordinal != null && (
          <span className="absolute right-1 top-1 rounded-full bg-ink/80 px-1.5 text-tiny font-bold text-white">
            {ordinal}
          </span>
        )}
      </div>
    )
  }

  return (
    <svg
      width={fullWidth ? undefined : size}
      height={fullWidth ? undefined : size}
      viewBox="0 0 96 96"
      className={fullWidth ? `h-auto w-full ${className ?? ''}` : className}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={gradient.from} />
          <stop offset="100%" stopColor={gradient.to} />
        </linearGradient>
      </defs>
      <rect width="96" height="96" rx="18" fill={`url(#${gradientId})`} />
      <circle cx="78" cy="18" r="26" fill="white" opacity="0.12" />
      <text
        x="48"
        y="50"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={label.length > 2 ? 34 : 52}
        fontWeight="800"
        fill="white"
        fontFamily="Inter, system-ui, sans-serif"
      >
        {label}
      </text>
    </svg>
  )
}
